import { messages } from "questionnaire-client";
import Vue from "vue";
import VueI18n from "vue-i18n";
import { castToVueI18n, createI18n } from "vue-i18n-bridge";
import { ref, Ref } from "@vue/composition-api";
import { ConfigClient } from "@/api/LumediPublicClient";

Vue.use(VueI18n, { bridge: true });

// Function to load locale messages
const localMessages = (() => {
  const locales = require.context("../locales", true, /[A-Za-z0-9-_,\s]+\.json$/i);
  const messages: VueI18n.LocaleMessages = {};
  locales.keys().forEach((key) => {
    const matched = key.match(/([A-Za-z0-9-_]+)\./i);
    if (matched && matched.length > 1) {
      const locale = matched[1];
      messages[locale] = locales(key);
    }
  });
  return messages;
})();

// Merge questionnaire messages with local messages.
//
// Every locale found is loaded (not just the build-time VUE_APP_LOCALE) so that
// setI18nLocale() can actually switch languages at runtime — a patient's stored
// PreferredLanguageCode isn't known until after login, so we can't rely on picking
// the right locale at build time the way this app originally did.
const mergedMessages = (() => {
  const result: VueI18n.LocaleMessages = {};

  const targetLocales = new Set<string>([
    ...Object.keys(localMessages),
    ...Object.keys(messages),
  ]);
  if (process.env.VUE_APP_LOCALE != undefined) {
    targetLocales.add(process.env.VUE_APP_LOCALE);
    if (process.env.VUE_APP_LOCALE.includes("-")) {
      targetLocales.add(process.env.VUE_APP_LOCALE.split('-')[0]);
    }
  }
  targetLocales.add("en-CA");
  targetLocales.add("en");

  for (const locale of targetLocales) {
    const questionnaire = messages[locale as keyof typeof messages];
    const local = localMessages[locale];
    if (questionnaire || local) {
      result[locale] = {
        ...(questionnaire ?? {}),
        ...(local ?? {}),
      };
    }
  }

  return result;
})();

export const i18n = castToVueI18n(
  createI18n(
    {
      legacy: false,
      locale: process.env.VUE_APP_LOCALE ?? "en-CA",
      fallbackLocale: "en",
      messages: mergedMessages,
      silentTranslationWarn: process.env.NODE_ENV === "production", // Suppress warnings in production
      missing: (locale, key) => {
        // Handle missing translations
        console.warn(`Missing translation: locale '${locale}', key '${key}'`);
        return key;
      },
    },
    VueI18n,
  ),
);

// Export translation function
export const t = (i18n as any).global.t as typeof i18n.t;

/**
 * Which language(s) this deployment currently allows the UI to render as - see
 * FeatureFlags.GetEnabledLanguages on the API. `null` until loadSupportedLanguages() resolves,
 * meaning "unknown yet, don't restrict anything" rather than failing closed to a single language
 * while that request is still in flight. A single-element array means "force this language,
 * hide any toggle"; a multi-element one means "show a toggle with exactly these options".
 * Exported as a ref (not a plain variable) so LanguageToggle.vue can render its options and
 * visibility reactively once this loads.
 */
export const supportedLanguages: Ref<string[] | null> = ref(null);

/** Display label for each language code this deployment could ever enable. */
export const languageLabels: Record<string, string> = {
  en: "English",
  fr: "Français",
  pt: "Português",
};

// Export locale setter function
export const setI18nLocale = (newLocale: string) => {
  // Single choke point for every locale change (login preference, ?language= query param,
  // manual toggle) - clamp to the enabled language(s) once known, so a deployment locked to one
  // language can't be routed to the other via any of those paths.
  const allowed = supportedLanguages.value;
  const baseLocale = newLocale.includes("-") ? newLocale.split("-")[0] : newLocale;
  const effectiveLocale = allowed && !allowed.includes(baseLocale) ? allowed[0] : newLocale;

  i18n.locale = effectiveLocale;
  (i18n as any).global.locale.value = effectiveLocale;

  // questionnaire-client (the shared Vue component library used to render interactions/
  // questionnaires, e.g. weight/height/BMI/lab-value entry) resolves its own locale
  // independently via sessionStorage's "culture" key - see
  // QuestionnaireEngine/questionnaire-client/src/utils/localeUtils.ts. It was never being set
  // here, so numeric input formatting stayed locked to its "en-CA" fallback regardless of the
  // patient's actual language. Mirrors the admin app's LanguageToggle.vue/HR site's userStore.ts,
  // which store the same full culture string ("en-CA"/"fr-CA") for the same reason.
  const culture = effectiveLocale.includes("-") ? effectiveLocale : `${effectiveLocale}-CA`;
  sessionStorage.setItem("culture", culture);
};

/**
 * Fetches which language(s) are enabled (GET /api/config/languages) and re-applies the current
 * locale so a single-language deployment locks in immediately - even if something (a
 * ?language= query param, a stored patient preference) already set a locale that turns out not
 * to be allowed by the time this resolves. Kicked off once at app boot (see main.ts).
 * Best-effort: a failed fetch just leaves the toggle unrestricted rather than blocking the app.
 */
export const loadSupportedLanguages = async (): Promise<void> => {
  try {
    const response = await new ConfigClient().getSupportedLanguages();
    supportedLanguages.value = response.languages?.length ? response.languages : ["en"];
  } catch (e) {
    console.warn("Failed to load supported languages", e);
    return;
  }
  setI18nLocale(i18n.locale);
};

Vue.use(i18n);

export default i18n;
